// What a compare's page reads from GitHub (docs/design.md "Subjects",
// "Compares"): the compare itself (`compare/base...head`, which the
// bridge allows), the repository's default branch when the URL names no
// base, the open pull request from the head when there is one, and each
// commit's pull requests (the linked pulls facet). Functions of the subject
// and an AbortSignal, as the pull request page's (pr/fetch.ts).
//
// GitHub's compare is read by name, a page at a time, and each page is
// worked out by GitHub against the branches as they are when it is asked.
// Every page names what it was worked out from: the base's tip
// (`base_commit`), the merge base, the commit count, and both ends' SHAs
// (`permalink_url`). Pages that disagree were read across a push, so the
// compare is read again, FILES_TRIES times at most (as fetchFiles reads
// a pull request's files), then it fails rather than mix two heads.
//
// GitHub lists a compare's commits oldest first, 250 at most without
// paging; paged, every one. The track holds the newest
// MAX_COMPARE_COMMITS (it is read from the head back, like a branch's
// window): a longer compare says so (`truncated`), and its track says
// older commits are not held (`hasOlder`). GitHub lists a compare's files
// on its first page only, 300 at most: past that, the rest are listed from
// the merge base's and the head's trees (`completeFiles`), each with no
// patch; when a tree is too large for GitHub to list whole, the files past
// 300 are left out, and `filesMissing` says so.
import { graphql, rest } from '../../api/gh';
import { HttpError } from '../../api/cache';
import type { PrFile } from '../../api/queries';
import type { Priority } from '../../api/scheduler';
import { GH, parseSubjectPath, subjectPath } from '../../router';
import { compareHead, type CompareSubject } from '../../subject';
import { FILES_TRIES } from '../pr/fetch';

/** The most commits a compare's track holds: the newest, as GitHub's own compare page lists 250. */
export const MAX_COMPARE_COMMITS = 250;
/** Commits per page of the compare (GitHub's most). */
export const COMPARE_PAGE = 100;
/** The most files GitHub's compare lists. */
export const MAX_COMPARE_FILES = 300;

/** A commit of the compare, as GitHub's REST compare lists it. */
export interface CompareCommit {
  sha: string;
  parents: string[];
  /** The whole commit message. */
  message: string;
  /** The commit's author: their login when GitHub links one, else the name git recorded. */
  author: string | null;
  authorName: string | null;
  authored: string | null;
  committed: string | null;
}

/** One read of a compare: what its page and its track are made from. */
export interface LoadedCompare {
  /** The base branch, as named or (the URL naming none) the repository's default branch. */
  baseRef: string;
  /** The repository's default branch, when it was read (a URL naming no base); else null. */
  defaultBranch: string | null;
  /** The base's tip when read. */
  baseSha: string;
  /** Where the head branched from the base: the track's base. */
  mergeBase: string;
  /** The head's SHA when read. */
  head: string;
  /** GitHub's word for how the head stands to the base. */
  status: 'ahead' | 'behind' | 'diverged' | 'identical';
  aheadBy: number;
  behindBy: number;
  /** Commits from the merge base to the head, as GitHub counts them. */
  totalCommits: number;
  /** The newest MAX_COMPARE_COMMITS of them at most, oldest first. */
  commits: CompareCommit[];
  /** Older commits than those held exist (more than MAX_COMPARE_COMMITS). */
  truncated: boolean;
  /** The files changed from the merge base to the head. */
  files: PrFile[];
  /** Files past GitHub's MAX_COMPARE_FILES that could not be listed (a tree too large to read whole); 0 when every file is listed. */
  filesMissing: number | null;
  /** Lines added and removed, over the files GitHub lists. */
  totals: { added: number; removed: number };
}

/** A compare page as GitHub sends it (what of it is read). */
interface ComparePage {
  status?: unknown;
  ahead_by?: unknown;
  behind_by?: unknown;
  total_commits?: unknown;
  permalink_url?: unknown;
  base_commit?: { sha?: unknown } | null;
  merge_base_commit?: { sha?: unknown } | null;
  commits?: unknown;
  files?: unknown;
}

/** Why a compare cannot be shown: GitHub has no such branch (or no common history), or does not show it. */
export class CompareNotFound extends Error {}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const SHA = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;

/**
 * The SHAs a compare page was worked out from, as its `permalink_url`
 * names them (`…/compare/[owner:]<base sha>...[owner:]<head sha>`); null
 * where it names none.
 */
export function permalinkEnds(url: unknown): { base: string; head: string } | null {
  const u = str(url);
  const at = u?.lastIndexOf('/compare/') ?? -1;
  if (!u || at < 0) return null;
  const [a, b, ...rest] = u.slice(at + '/compare/'.length).split('...');
  if (b === undefined || rest.length) return null;
  const sha = (end: string) => end.slice(end.lastIndexOf(':') + 1).toLowerCase();
  const base = sha(a), head = sha(b);
  return SHA.test(base) && SHA.test(head) ? { base, head } : null;
}

/** A commit as GitHub's REST lists it (a compare's, a branch's history), or null when it names none. */
export function readCommit(v: unknown): CompareCommit | null {
  const c = v as { sha?: unknown; parents?: unknown; commit?: { message?: unknown; author?: { name?: unknown; date?: unknown } | null; committer?: { date?: unknown } | null } | null; author?: { login?: unknown } | null };
  const sha = str(c?.sha);
  if (!sha) return null;
  const parents = Array.isArray(c.parents) ? c.parents.flatMap((p) => (typeof p?.sha === 'string' ? [p.sha as string] : [])) : [];
  const authorName = str(c.commit?.author?.name);
  return {
    sha,
    parents,
    message: str(c.commit?.message) ?? '',
    author: str(c.author?.login) ?? authorName,
    authorName,
    authored: str(c.commit?.author?.date),
    committed: str(c.commit?.committer?.date),
  };
}

const readFiles = (v: unknown): PrFile[] => (Array.isArray(v) ? (v as PrFile[]).filter((f) => typeof f?.filename === 'string') : []);

/** What every page of one compare agrees on, when read at one moment. */
const agreement = (p: ComparePage) =>
  JSON.stringify([str(p.base_commit?.sha), str(p.merge_base_commit?.sha), num(p.total_commits), permalinkEnds(p.permalink_url)?.head ?? null]);

/** The pages holding the newest `max` of `total` commits, `per` to a page (1-based, oldest first). */
export function newestPages(total: number, per = COMPARE_PAGE, max = MAX_COMPARE_COMMITS): number[] {
  if (total <= per) return [1];
  const first = Math.floor(Math.max(0, total - max) / per) + 1;
  const last = Math.ceil(total / per);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

export interface CompareReads {
  priority: Priority;
  signal: AbortSignal;
}

/** The repository's default branch. */
export async function defaultBranch(owner: string, repo: string, opts: CompareReads): Promise<string> {
  try {
    const r = await rest<{ default_branch?: unknown }>(`repos/${owner}/${repo}`, opts);
    const name = str(r?.default_branch);
    if (name) return name;
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) throw new CompareNotFound(`GitHub doesn't show ${owner}/${repo} to you.`);
    throw e;
  }
  throw new Error(`GitHub did not say what ${owner}/${repo}'s default branch is.`);
}

/** A branch name in a REST path: percent-encoded but for its slashes (GitHub reads `feat/x` as the branch). */
const refPath = (ref: string) => encodeURIComponent(ref).replace(/%2F/gi, '/');

/** The compare's `base...head` in a REST path, the head as GitHub writes a fork's (`ana:feat`). */
export function compareSpec(subject: CompareSubject, base: string): string {
  return `${refPath(base)}...${compareHead({ ...subject, head: refPath(subject.head) })}`;
}

/**
 * The compare: its ends' SHAs, the merge base, its commits (the newest
 * MAX_COMPARE_COMMITS) and files, read so that every page is of one head
 * (see the file comment). `base` is the base branch to compare with (the
 * default branch, read by the caller, when the subject names none).
 * Throws CompareNotFound when GitHub has no such branch, no common history
 * between them, or does not show the repository.
 */
export async function fetchCompare(subject: CompareSubject, base: string, opts: CompareReads, onListed: (files: number) => void = () => {}): Promise<Omit<LoadedCompare, 'defaultBranch'>> {
  const path = `repos/${subject.owner}/${subject.repo}/compare/${compareSpec(subject, base)}`;
  const page = async (n: number): Promise<ComparePage> => {
    try {
      return await rest<ComparePage>(`${path}?per_page=${COMPARE_PAGE}&page=${n}`, opts);
    } catch (e) {
      if (e instanceof HttpError && e.status === 404)
        throw new CompareNotFound(`GitHub has no compare of ${base} with ${compareHead(subject)} in ${subject.owner}/${subject.repo}: a branch is missing, they share no history, or GitHub doesn't show it to you.`);
      throw e;
    }
  };
  for (let tries = 1; ; tries++) {
    const first = await page(1);
    onListed(readFiles(first.files).length);
    const total = num(first.total_commits);
    const pages = newestPages(total).filter((n) => n !== 1);
    const rest_ = await Promise.all(pages.map(page));
    const all = [first, ...rest_];
    if (all.every((p) => agreement(p) === agreement(first))) return assemble(subject, base, first, newestPages(total).includes(1) ? all : rest_);
    if (tries >= FILES_TRIES) throw new Error(`${compareHead(subject)} kept changing while it was read. Try again in a moment.`);
  }
}

/** One compare from its first page (what the whole says, and its files) and the pages holding its newest commits, oldest first. */
function assemble(subject: CompareSubject, base: string, first: ComparePage, pages: ComparePage[]): Omit<LoadedCompare, 'defaultBranch'> {
  const baseSha = str(first.base_commit?.sha);
  const mergeBase = str(first.merge_base_commit?.sha);
  if (!baseSha || !mergeBase) throw new Error(`GitHub did not say where ${compareHead(subject)} branches from ${base}.`);
  const total = num(first.total_commits);
  const listed = pages.flatMap((p) => (Array.isArray(p.commits) ? p.commits : []).map(readCommit).filter((c): c is CompareCommit => c !== null));
  // The newest MAX_COMPARE_COMMITS (the pages may hold a few more of the older side).
  const commits = listed.slice(Math.max(0, listed.length - MAX_COMPARE_COMMITS));
  // The head the pages were worked out from; without a permalink, the newest commit listed (no commits: the merge base, which the head then is).
  const head = permalinkEnds(first.permalink_url)?.head ?? commits[commits.length - 1]?.sha ?? mergeBase;
  const status = str(first.status);
  const files = readFiles(first.files);
  return {
    baseRef: base,
    baseSha,
    mergeBase,
    head,
    status: status === 'ahead' || status === 'behind' || status === 'diverged' || status === 'identical' ? status : total ? 'ahead' : 'identical',
    aheadBy: num(first.ahead_by),
    behindBy: num(first.behind_by),
    totalCommits: total,
    commits,
    truncated: commits.length < total,
    files,
    filesMissing: 0,
    totals: files.reduce((t, f) => ({ added: t.added + num(f.additions), removed: t.removed + num(f.deletions) }), { added: 0, removed: 0 }),
  };
}

/** A commit's tree, path → blob SHA; null when GitHub cut it short (too large to list whole). */
export async function treeOf(owner: string, repo: string, sha: string, opts: CompareReads): Promise<Map<string, string> | null> {
  const t = await rest<{ truncated?: boolean; tree?: { path: string; type: string; sha: string }[] }>(`repos/${owner}/${repo}/git/trees/${sha}?recursive=1`, opts);
  return t.truncated || !Array.isArray(t.tree) ? null : new Map(t.tree.filter((e) => e.type === 'blob').map((e) => [e.path, e.sha]));
}

/**
 * The compare's files when GitHub listed MAX_COMPARE_FILES (it lists no
 * more): GitHub's, then every other path whose blob differs between the
 * merge base's tree and the head's, as added, removed or modified, with no
 * patch and no line counts (a rename past the cap reads as a removal and
 * an addition). `missing` is null when a tree was too large to read whole:
 * the files past GitHub's are then not known.
 */
export async function completeFiles(owner: string, repo: string, mergeBase: string, head: string, listed: PrFile[], opts: CompareReads): Promise<{ files: PrFile[]; missing: number | null }> {
  if (listed.length < MAX_COMPARE_FILES) return { files: listed, missing: 0 };
  const [from, to] = await Promise.all([treeOf(owner, repo, mergeBase, opts), treeOf(owner, repo, head, opts)]);
  if (!from || !to) return { files: listed, missing: null };
  const seen = new Set(listed.flatMap((f) => (f.previous_filename ? [f.filename, f.previous_filename] : [f.filename])));
  const more: PrFile[] = [];
  for (const path of [...new Set([...from.keys(), ...to.keys()])].sort()) {
    const a = from.get(path) ?? null, b = to.get(path) ?? null;
    if (a === b || seen.has(path)) continue;
    more.push({ filename: path, sha: b, status: !a ? 'added' : !b ? 'removed' : 'modified', additions: 0, deletions: 0, changes: 1 });
  }
  return { files: [...listed, ...more], missing: 0 };
}

/** The open pull request from the compare's head into this repository, when there is one: its number, title and base branch. */
export interface HeadPull {
  number: number;
  title: string;
  base: string;
}

/**
 * The open pull requests from the head branch (GitHub's `pulls?head=`,
 * which names a head by its owner and branch), the one into `base` first.
 * Empty when there is none.
 */
export async function headPulls(subject: CompareSubject, base: string, opts: CompareReads): Promise<HeadPull[]> {
  const pulls = await pullsFrom(subject.owner, subject.repo, subject.headRepo?.owner ?? subject.owner, subject.head, opts);
  return pulls.sort((a, b) => Number(b.base === base) - Number(a.base === base));
}

/**
 * The open pull requests into `owner/repo` from branch `branch` of
 * `headOwner`'s repository (GitHub's `pulls?head=`), in GitHub's order;
 * empty when there is none.
 */
export async function pullsFrom(owner: string, repo: string, headOwner: string, branch: string, opts: CompareReads): Promise<HeadPull[]> {
  const list = await rest<unknown>(`repos/${owner}/${repo}/pulls?state=open&per_page=10&head=${encodeURIComponent(`${headOwner}:${branch}`)}`, opts);
  return (Array.isArray(list) ? list : []).flatMap((p): HeadPull[] => {
    const number = (p as { number?: unknown }).number, title = (p as { title?: unknown }).title, ref = (p as { base?: { ref?: unknown } }).base?.ref;
    return typeof number === 'number' && typeof title === 'string' && typeof ref === 'string' ? [{ number, title, base: ref }] : [];
  });
}

/** A pull request that brought a commit in (the linked pulls facet). */
export interface LinkedPull {
  number: number;
  title: string;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  /** Its page on GitHub. */
  url: string;
  /** Its page in Changeling (GitHub's path under `/gh`), or null when GitHub's URL names no pull request. */
  path: string | null;
}

/** The Changeling page of the pull request at GitHub URL `url` (its path with `github.com` swapped for `/gh`), or null. */
export function pullPath(url: string): string | null {
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return null;
  }
  const at = parseSubjectPath(GH + pathname);
  return at && 'subject' in at && at.subject.kind === 'pull' ? subjectPath(at.subject) : null;
}

/** The pull requests GraphQL's `associatedPullRequests { nodes { number title state url } }` names. */
export function readLinked(nodes: readonly unknown[]): LinkedPull[] {
  return nodes.flatMap((n): LinkedPull[] => {
    const p = n as Partial<LinkedPull> | null;
    return p && typeof p.number === 'number' && typeof p.title === 'string' && typeof p.url === 'string' && (p.state === 'OPEN' || p.state === 'CLOSED' || p.state === 'MERGED')
      ? [{ number: p.number, title: p.title, state: p.state, url: p.url, path: pullPath(p.url) }]
      : [];
  });
}

/** The most commits whose pull requests one load asks for: the newest. Older ones show none. */
export const MAX_LINKED_COMMITS = 100;
/** Commits per GraphQL request (each is one aliased `object`, a point at most together). */
const LINKED_BATCH = 50;
/** Pull requests per commit. */
const LINKED_PER_COMMIT = 3;

function linkedQuery(n: number): string {
  const fields = Array.from(
    { length: n },
    (_, i) => `c${i}: object(oid: $c${i}) { ... on Commit { associatedPullRequests(first: ${LINKED_PER_COMMIT}) { nodes { number title state url } } } }`,
  ).join('\n    ');
  const vars = Array.from({ length: n }, (_, i) => `$c${i}: GitObjectID!`).join(', ');
  return `query CompareLinkedPulls($owner: String!, $repo: String!, ${vars}) {\n  repository(owner: $owner, name: $repo) {\n    ${fields}\n  }\n}`;
}

/**
 * The pull requests that brought each of `shas` in (GraphQL's
 * `associatedPullRequests`, LINKED_PER_COMMIT per commit), the newest
 * MAX_LINKED_COMMITS of them at most, LINKED_BATCH to a request: two
 * requests at most, through the app's scheduler at the caller's priority
 * like the page's other reads (the relay's GraphQL budget is the agents',
 * which a person's page does not spend). A commit GitHub says nothing of
 * has none.
 */
export async function fetchLinkedPulls(
  owner: string,
  repo: string,
  shas: readonly string[],
  opts: CompareReads,
): Promise<Map<string, LinkedPull[]>> {
  const out = new Map<string, LinkedPull[]>();
  const asked = shas.slice(Math.max(0, shas.length - MAX_LINKED_COMMITS));
  for (let i = 0; i < asked.length; i += LINKED_BATCH) {
    const chunk = asked.slice(i, i + LINKED_BATCH);
    const vars: Record<string, unknown> = { owner, repo };
    chunk.forEach((sha, j) => (vars['c' + j] = sha));
    const res = await graphql<{ repository: Record<string, { associatedPullRequests?: { nodes?: unknown[] } } | null> | null }>(linkedQuery(chunk.length), vars, { ...opts, partial: true });
    chunk.forEach((sha, j) => {
      const nodes = res?.repository?.['c' + j]?.associatedPullRequests?.nodes ?? [];
      out.set(sha, readLinked(nodes));
    });
  }
  return out;
}

/**
 * Everything a compare's page and track are made from: the default branch
 * when the subject names no base, then the compare, with its files past
 * GitHub's cap listed from the trees (`completeFiles`). `listed` hears how
 * many files GitHub's first page listed, as soon as it is read.
 */
export async function loadCompare(subject: CompareSubject, opts: CompareReads, listed: (files: number) => void = () => {}): Promise<LoadedCompare> {
  const dflt = subject.base === null ? await defaultBranch(subject.owner, subject.repo, opts) : null;
  const read = await fetchCompare(subject, subject.base ?? dflt!, opts, listed);
  const { files, missing } = await completeFiles(subject.owner, subject.repo, read.mergeBase, read.head, read.files, opts);
  return { ...read, defaultBranch: dflt, files, filesMissing: missing };
}
